/**
 * Configuración → Bancos (`/configuracion/bancos`).
 *
 * Puerto de `Bank` + `views/bank/index.php`: el listado con alta, edición y baja
 * en un modal, igual que el panel anterior. El alta sólo tenía una descripción;
 * aquí se añade el estado (`banks.active`), que la tabla ya guardaba pero la
 * pantalla no mostraba.
 */

import { ArrowLeft, Building2, Pencil, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatDateValue } from '@/lib/utils';

import { useBancos, useMutarBancos } from '../catalogos.api';
import type { Banco } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Formulario de alta/edición (un solo campo, como el modal del panel anterior). */
function BancoDialogo({
  banco,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  banco: Banco | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (descripcion: string) => void;
  onCerrar: () => void;
}) {
  const [descripcion, setDescripcion] = useState(banco?.descripcion.trim() ?? '');
  const valido = descripcion.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={banco ? 'Editar banco' : 'Nuevo banco'}
      description={
        banco
          ? `Banco #${banco.id}. La descripción es la que aparece al elegir la cuenta.`
          : 'El banco se podrá elegir al registrar una cuenta bancaria.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() => onGuardar(descripcion.trim())}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <Input
          label="Descripción"
          value={descripcion}
          maxLength={255}
          autoFocus
          placeholder="Ej. BCP"
          onChange={(evento) => setDescripcion(evento.target.value)}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter' && valido && !guardando) onGuardar(descripcion.trim());
          }}
        />

        {banco ? (
          <p className="text-xs text-ink-500">
            Última modificación: {formatDateValue(banco.actualizado_en) || '—'}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

export function BancosPage() {
  useDocumentTitle('Configuración · Bancos');

  const bancos = useBancos();
  const mutaciones = useMutarBancos();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; banco: Banco | null }>({
    abierto: false,
    banco: null,
  });
  const [porEliminar, setPorEliminar] = useState<Banco | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = bancos.error instanceof ApiError && bancos.error.status === 403;
  const lista = bancos.data?.bancos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Building2 className="size-6 text-brand-500" aria-hidden="true" />
          Bancos
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Entidades bancarias disponibles al registrar las cuentas de la empresa.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, banco: null });
        }}
      >
        Nuevo banco
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (descripcion: string) => {
    setErrorDialogo(null);

    if (dialogo.banco) {
      mutaciones.actualizar.mutate(
        { id: dialogo.banco.id, descripcion },
        {
          onSuccess: (datos) => {
            setDialogo({ abierto: false, banco: null });
            setAviso({ tono: 'success', texto: datos.mensaje });
          },
          onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar el banco.')),
        },
      );
      return;
    }

    mutaciones.crear.mutate(descripcion, {
      onSuccess: (datos) => {
        setDialogo({ abierto: false, banco: null });
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar el banco.')),
    });
  };

  const eliminar = () => {
    if (!porEliminar) return;

    mutaciones.eliminar.mutate(porEliminar.id, {
      onSuccess: (datos) => {
        setPorEliminar(null);
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => {
        setPorEliminar(null);
        setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo eliminar el banco.') });
      },
    });
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {bancos.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : bancos.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los bancos">
          {mensajeDe(bancos.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Lista de bancos</CardTitle>
            <Badge variant="neutral">{bancos.data?.total ?? 0} registrados</Badge>
          </CardHeader>
          <CardContent>
            {lista.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-500">
                Todavía no hay bancos registrados.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[36rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                      <th className="w-16 pb-2">#</th>
                      <th className="pb-2">Descripción</th>
                      <th className="w-28 pb-2">Estado</th>
                      <th className="w-24 pb-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map((banco) => (
                      <tr key={banco.id} className="border-t border-ink-100">
                        <td className="py-3 text-ink-500">{banco.id}</td>
                        <td className="py-3 font-medium text-ink-900">{banco.descripcion}</td>
                        <td className="py-3">
                          <Badge variant={banco.activo ? 'success' : 'neutral'}>
                            {banco.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Editar ${banco.descripcion}`}
                              title="Editar"
                              onClick={() => {
                                setErrorDialogo(null);
                                setDialogo({ abierto: true, banco });
                              }}
                            >
                              <Pencil className="size-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Eliminar ${banco.descripcion}`}
                              title="Eliminar"
                              onClick={() => setPorEliminar(banco)}
                            >
                              <Trash2 className="size-4 text-rose-500" aria-hidden="true" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <BancoDialogo
          key={`banco-${dialogo.banco?.id ?? 'nuevo'}`}
          banco={dialogo.banco}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, banco: null })}
        />
      ) : null}

      <ConfirmDialog
        open={porEliminar !== null}
        title="¿Eliminar el banco?"
        description={`Se eliminará «${porEliminar?.descripcion.trim() ?? ''}». No se puede eliminar un banco con cuentas registradas.`}
        confirmLabel="Sí, eliminar"
        loading={mutaciones.eliminar.isPending}
        onClose={() => setPorEliminar(null)}
        onConfirm={eliminar}
      />
    </div>
  );
}
